import React, { useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { LeaveStatusBadge } from '../../components/LeaveStatusBadge';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { getAttendanceCorrectionModuleCapabilities } from '../../config/attendanceCorrectionCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useWorkflowInbox } from '../../hooks/useWorkflowInbox';
import type { AdminAttendanceCorrectionStatusTab } from '../../types/attendanceCorrection';
import type { WorkflowInboxItem } from '../../types/workflow';
import { attendanceCorrectionApprovalListTitle } from '../../utils/approvalRoles';
import { formatAppliedOn } from '../../utils/dateTime';
import { isRejectedWorkflowStatus } from '../../utils/workflowStatus';

type Props = NativeStackScreenProps<
  RootStackParamList,
  'AdminAttendanceCorrectionRequests'
>;

const STATUS_TABS: readonly AdminAttendanceCorrectionStatusTab[] = [
  'Pending',
  'Approved',
  'Rejected',
  'All',
];

function matchesStatusTab(
  item: WorkflowInboxItem,
  tab: AdminAttendanceCorrectionStatusTab,
): boolean {
  if (tab === 'All') return true;
  const code = (item.workflowStatusCode || '').toUpperCase();
  const label = item.workflowStatusLabel.toLowerCase();
  if (tab === 'Pending') {
    return (
      code === 'PENDING' ||
      code === 'IN_PROGRESS' ||
      label.includes('pending') ||
      label.includes('progress')
    );
  }
  if (tab === 'Approved') {
    return (
      code === 'APPROVED' || code === 'COMPLETED' || label.includes('approved')
    );
  }
  return code === 'REJECTED' || label.includes('rejected');
}

function isCorrectionRequest(item: WorkflowInboxItem): boolean {
  const code = (item.requestTypeCode || '').toUpperCase();
  return code === 'ATTENDANCE_CORRECTION';
}

function timeRangeLabel(item: WorkflowInboxItem): string {
  if (item.requestSummary?.trim()) return item.requestSummary.trim();
  return 'Times unavailable';
}

function dateLabel(item: WorkflowInboxItem): string {
  const appliedOn = formatAppliedOn(item.submittedAt);
  if (appliedOn !== 'Not available') return appliedOn;
  return 'Date unavailable';
}

function CorrectionRequestRow({
  item,
  onPress,
  palette,
}: {
  item: WorkflowInboxItem;
  onPress: () => void;
  palette: ColorPalette;
}) {
  const employeeName =
    item.employee.displayName ||
    item.employee.employeeNumber ||
    'Employee unavailable';

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${employeeName}, ${timeRangeLabel(item)}, ${
        item.workflowStatusLabel
      }`}
      onPress={onPress}
      style={[styles.row, { borderBottomColor: palette.border }]}
    >
      <View style={styles.rowMain}>
        <Text style={[styles.employeeName, { color: palette.textPrimary }]}>
          {employeeName}
        </Text>
        <Text style={[styles.meta, { color: palette.textSecondary }]}>
          {dateLabel(item)}
        </Text>
        <Text style={[styles.meta, { color: palette.textSecondary }]}>
          {timeRangeLabel(item)}
        </Text>
        {isRejectedWorkflowStatus(item.workflowStatusCode) &&
        item.rejectReason?.trim() ? (
          <Text
            style={[styles.meta, { color: palette.textSecondary }]}
            numberOfLines={1}
            ellipsizeMode="tail"
            {...(Platform.OS === 'web'
              ? { title: item.rejectReason.trim() }
              : null)}
          >
            Reject Reason: {item.rejectReason.trim()}
          </Text>
        ) : null}
      </View>
      <LeaveStatusBadge
        label={item.workflowStatusLabel}
        statusCode={item.workflowStatusCode}
      />
    </Pressable>
  );
}

function StatusTabs({
  value,
  counts,
  onChange,
  palette,
}: {
  value: AdminAttendanceCorrectionStatusTab;
  counts: Record<AdminAttendanceCorrectionStatusTab, number>;
  onChange: (tab: AdminAttendanceCorrectionStatusTab) => void;
  palette: ColorPalette;
}) {
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.tabsContent}
      style={styles.tabsScroll}
    >
      {STATUS_TABS.map(tab => {
        const selected = tab === value;
        const count = counts[tab];
        const label =
          tab === 'Pending' && count > 0 ? `${tab} (${count})` : tab;
        return (
          <Pressable
            key={tab}
            accessibilityRole="button"
            accessibilityState={{ selected }}
            accessibilityLabel={label}
            onPress={() => onChange(tab)}
            style={[
              styles.tab,
              {
                backgroundColor: selected
                  ? `${palette.info}18`
                  : palette.surface,
                borderColor: selected ? palette.info : palette.border,
              },
            ]}
          >
            <Text
              style={[
                styles.tabLabel,
                { color: selected ? palette.info : palette.textPrimary },
              ]}
            >
              {label}
            </Text>
          </Pressable>
        );
      })}
    </ScrollView>
  );
}

export function AdminAttendanceCorrectionRequestListScreen({
  navigation,
}: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const capabilities = getAttendanceCorrectionModuleCapabilities(
    auth.permissions,
  );
  const [statusTab, setStatusTab] =
    useState<AdminAttendanceCorrectionStatusTab>('Pending');
  const inbox = useWorkflowInbox();
  const {
    applyFilters,
    refreshInbox,
    retry,
    items,
    isLoading,
    isRefreshing,
    isLoadingMore,
    error,
    loadMore,
  } = inbox;

  useFocusEffect(
    useCallback(() => {
      if (!capabilities.canViewAdminRequests) return;
      applyFilters({
        entityType: 'ATTENDANCE_CORRECTION',
        status:
          statusTab === 'All'
            ? 'ALL'
            : (statusTab.toUpperCase() as 'PENDING' | 'APPROVED' | 'REJECTED'),
      });
    }, [applyFilters, capabilities.canViewAdminRequests, statusTab]),
  );

  const correctionItems = useMemo(
    () => items.filter(isCorrectionRequest),
    [items],
  );

  const counts = useMemo(() => {
    const next: Record<AdminAttendanceCorrectionStatusTab, number> = {
      Pending: 0,
      Approved: 0,
      Rejected: 0,
      All: correctionItems.length,
    };
    for (const item of correctionItems) {
      if (matchesStatusTab(item, 'Pending')) next.Pending += 1;
      if (matchesStatusTab(item, 'Approved')) next.Approved += 1;
      if (matchesStatusTab(item, 'Rejected')) next.Rejected += 1;
    }
    return next;
  }, [correctionItems]);

  const visibleItems = useMemo(
    () => correctionItems.filter(item => matchesStatusTab(item, statusTab)),
    [correctionItems, statusTab],
  );

  const footer = useMemo(() => {
    if (!isLoadingMore) return null;
    return (
      <View
        style={styles.footer}
        accessibilityLabel="Loading more attendance correction requests"
      >
        <ActivityIndicator color={palette.primary} />
      </View>
    );
  }, [isLoadingMore, palette.primary]);

  const emptyMessage =
    statusTab === 'Pending'
      ? 'There are no pending attendance correction requests waiting for approval.'
      : `No ${statusTab.toLowerCase()} attendance correction requests were found.`;

  return (
    <PermissionGuard
      permission={PERMISSIONS.ATTENDANCE_APPROVE}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen contentStyle={styles.screen}>
        <ScreenHeader
          title={attendanceCorrectionApprovalListTitle(auth.roles)}
          onBack={navigation.goBack}
        />
        <View style={styles.body}>
          <StatusTabs
            value={statusTab}
            counts={counts}
            onChange={setStatusTab}
            palette={palette}
          />

          {isLoading && correctionItems.length === 0 ? (
            <View style={styles.center}>
              <ActivityIndicator
                color={palette.primary}
                accessibilityLabel="Loading attendance correction requests"
              />
            </View>
          ) : error && correctionItems.length === 0 ? (
            <ErrorState message={error} onRetry={retry} />
          ) : (
            <FlatList
              data={visibleItems}
              keyExtractor={item => String(item.workflowInstanceId)}
              refreshControl={
                <RefreshControl
                  refreshing={isRefreshing}
                  onRefresh={refreshInbox}
                  tintColor={palette.primary}
                />
              }
              onEndReached={loadMore}
              onEndReachedThreshold={0.4}
              ListFooterComponent={footer}
              ListEmptyComponent={
                <EmptyState
                  title="No correction requests"
                  message={emptyMessage}
                  actionTitle="Retry"
                  onAction={retry}
                />
              }
              contentContainerStyle={styles.listContent}
              renderItem={({ item }) => (
                <CorrectionRequestRow
                  item={item}
                  palette={palette}
                  onPress={() =>
                    navigation.navigate(routes.attendanceCorrectionApproval, {
                      workflowInstanceId: item.workflowInstanceId,
                    })
                  }
                />
              )}
            />
          )}
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, paddingBottom: 0 },
  body: { flex: 1, marginTop: spacing.md },
  tabsScroll: { flexGrow: 0, marginBottom: spacing.md },
  tabsContent: {
    gap: spacing.sm,
    paddingRight: spacing.md,
  },
  tab: {
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  tabLabel: {
    ...typography.caption,
    fontWeight: '700',
  },
  listContent: { flexGrow: 1, paddingBottom: spacing.xl },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.md,
    paddingVertical: spacing.lg,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  rowMain: { flex: 1, gap: spacing.xs },
  employeeName: { ...typography.body, fontWeight: '700' },
  meta: { ...typography.caption },
  footer: { paddingVertical: spacing.lg, alignItems: 'center' },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
});
